/**
 * SearchableUserDropdown Component
 * 
 * Searchable dropdown for filtering matches by assigned user.
 * Features:
 * - Case-insensitive search by user name
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - "All Users" option always visible
 * - Accessible with ARIA attributes
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { UniqueUser } from '../../utils/matchFilters';

interface SearchableUserDropdownProps {
  users: UniqueUser[];
  selectedUserId: string | null;
  onChange: (userId: string | null) => void;
}

const SearchableUserDropdown: React.FC<SearchableUserDropdownProps> = ({
  users,
  selectedUserId,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter users based on search query (case-insensitive)
  const filteredUsers = searchQuery.trim()
    ? users.filter(user => 
        user.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  // Get selected user name for display
  const selectedUserName = selectedUserId
    ? users.find(u => u.id === selectedUserId)?.name || 'Unknown User'
    : 'All Users';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  };

  const handleSelect = (userId: string | null) => {
    onChange(userId);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    // Total options: "All Users" + filtered users
    const totalOptions = filteredUsers.length + 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex === 0) {
          // "All Users" option
          handleSelect(null);
        } else if (highlightedIndex > 0 && highlightedIndex <= filteredUsers.length) {
          // User option
          handleSelect(filteredUsers[highlightedIndex - 1].id);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;

      case 'Tab':
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;

      default:
        // Reset highlight when typing
        if (e.key.length === 1) {
          setHighlightedIndex(0);
        }
        break;
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setHighlightedIndex(-1);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className="w-full px-3 py-1.5 text-xs text-left text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Filter by assigned user"
      >
        <span className="truncate">{selectedUserName}</span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search users..."
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-label="Search users"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <ul
            ref={listRef}
            role="listbox"
            className="max-h-60 overflow-y-auto py-1"
            aria-label="User options"
          >
            {/* "All Users" Option - Always visible */}
            <li
              role="option"
              aria-selected={selectedUserId === null}
              onClick={() => handleSelect(null)}
              onMouseEnter={() => setHighlightedIndex(0)}
              className={`px-4 py-2 text-sm cursor-pointer ${
                highlightedIndex === 0
                  ? 'bg-blue-50 text-blue-700'
                  : selectedUserId === null
                  ? 'bg-gray-50 text-gray-900 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              All Users
            </li>

            {/* User Options */}
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user, index) => {
                const optionIndex = index + 1; // +1 because "All Users" is at index 0
                const isSelected = selectedUserId === user.id;
                const isHighlighted = highlightedIndex === optionIndex;

                return (
                  <li
                    key={user.id}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(user.id)}
                    onMouseEnter={() => setHighlightedIndex(optionIndex)}
                    className={`px-4 py-2 text-sm cursor-pointer ${
                      isHighlighted
                        ? 'bg-blue-50 text-blue-700'
                        : isSelected
                        ? 'bg-gray-50 text-gray-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {user.name}
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-2 text-sm text-gray-500 italic">
                No users found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableUserDropdown;
